import {useMultiStyleConfig} from '@chakra-ui/react';
import type {StyleFunctionProps} from '@chakra-ui/styled-system';
import {createMultiStyleConfigHelpers, defineCssVars} from '@chakra-ui/styled-system';
import {mode} from '@chakra-ui/theme-tools';

import {semantic as semanticDark, components as componentsDark} from '../../../design-tokens/dark';
import {core, semantic, components} from '../../../design-tokens/light';
import type {Intent} from '../../intent';
import {getScheme} from '../../intent';
import {typography} from '../../tokens';
import type {MultipartStyles} from '../../types';
import {getCoreValueAsThemeToken} from '../../types';

const anatomy = [`group`, `item`, `itemIcon`] as const;
export type Parts = (typeof anatomy)[number];

const css = defineCssVars(`radio-button-group`, [`item-border`]);

const tokens = components.colors.components[`radio-button-group`];
const darkTokens = componentsDark.colors.components[`radio-button-group`];

const {defineMultiStyleConfig, definePartsStyle} = createMultiStyleConfigHelpers(anatomy);

const baseStyleGroup = (props: StyleFunctionProps) => {
  return {
    borderRadius: getCoreValueAsThemeToken(core, `radii.radius-rounded`),
    // border: `1px solid`,
    // borderColor: mode(semantic.colors.stroke.tertiary, semanticDark.colors.stroke.tertiary)(props),
    color: mode(semantic.colors.icon.secondary, semanticDark.colors.icon.secondary)(props),
    bg: mode(semantic.colors.background.primary, semanticDark.colors.background.primary)(props),
    display: `inline-flex`,
    alignItems: `flex-start`,
    flexWrap: `nowrap`,
    ...typography.body.sm,
  };
};

const baseStyleItem = (props: StyleFunctionProps & {intent?: Intent}) => {
  const {intent} = props;
  const scheme = intent ? getScheme({intent, props}) : {color: ``, background: ``};
  return {
    // Layout
    height: `32px`,
    px: getCoreValueAsThemeToken(core, `space.space-100`),
    py: getCoreValueAsThemeToken(core, `space.space-200`),
    gap: getCoreValueAsThemeToken(core, `space.space-100`),
    display: `flex`,
    alignItems: `center`,
    justifyContent: `center`,
    flexShrink: 0,
    // Border
    border: `1px solid`,
    borderColor: css[`item-border`].reference,
    background: mode(tokens[`background-default`], darkTokens[`background-default`])(props),
    color: mode(tokens[`text-default`], darkTokens[`text-default`])(props),

    [css[`item-border`].variable]: `colors.${mode(tokens[`stroke-default`], darkTokens[`stroke-default`])(props)}`,

    [`&[data-intent="${intent}"]`]: {
      [`&[data-checked]`]: {
        zIndex: 1,
        [css[`item-border`].variable]: `${scheme?.color}`,
        color: scheme?.color,
        background: scheme?.background,
      },
      [`&[data-active]`]: {
        [css[`item-border`].variable]: `colors.${mode(tokens[`stroke-default`], darkTokens[`stroke-default`])(props)}`,
        color: scheme?.color,
      },
      [`&[data-hover]`]: {
        [css[`item-border`].variable]: `colors.${mode(tokens[`stroke-default`], darkTokens[`stroke-default`])(props)}`,
      },
    },

    _hover: {
      [`&:not(:disabled)`]: {
        background: mode(tokens[`background-hover`], darkTokens[`background-hover`])(props),
      },
    },

    _active: {
      [`&:not(:disabled)`]: {
        background: mode(tokens[`background-pressed`], darkTokens[`background-pressed`])(props),
        color: mode(tokens[`text-default`], darkTokens[`text-default`])(props),
      },
    },

    _focus: {
      [`&:not(:disabled)`]: {
        background: mode(tokens[`background-hover`], darkTokens[`background-hover`])(props),
        borderColor: mode(tokens[`stroke-focus`], darkTokens[`stroke-focus`])(props),
        zIndex: 1,
      },
    },

    _disabled: {
      background: mode(tokens[`background-disabled`], darkTokens[`background-disabled`])(props),
      borderColor: mode(tokens[`stroke-default`], darkTokens[`stroke-default`])(props),
      color: mode(tokens[`text-disabled`], darkTokens[`text-disabled`])(props),
      cursor: `not-allowed`,
    },

    [`&[data-first="true"]`]: {
      borderTopLeftRadius: getCoreValueAsThemeToken(core, `radii.radius-1000`),
      borderBottomLeftRadius: getCoreValueAsThemeToken(core, `radii.radius-1000`),
    },
    [`&[data-last="true"]`]: {
      borderTopRightRadius: getCoreValueAsThemeToken(core, `radii.radius-1000`),
      borderBottomRightRadius: getCoreValueAsThemeToken(core, `radii.radius-1000`),
    },
  };
};

const baseStyleItemIcon = (props: StyleFunctionProps) => {
  const {intent} = props;
  const scheme = intent ? getScheme({intent, props}) : {color: ``, background: ``};
  const color = mode(tokens[`icon-default`], darkTokens[`icon-default`])(props);

  return {
    color,
    borderRadius: getCoreValueAsThemeToken(core, `radii.radius-rounded`),
    p: `2px`,
    [`&[data-intent="${intent}"]`]: {
      [`&[data-checked=true]`]: {
        color: scheme?.color,
        background: scheme?.background,
      },
      [`&[data-active]`]: {
        color: scheme?.color,
        bgColor: scheme?.background,
      },
      [`&[data-hover]`]: {
        color: scheme?.color,
        bgColor: scheme?.background,
      },
    },
    _disabled: {
      color: mode(tokens[`icon-disabled`], darkTokens[`icon-disabled`])(props),
    },
  };
};

export const radioButtonGroupTheme = defineMultiStyleConfig({
  baseStyle: definePartsStyle(props => ({
    group: baseStyleGroup(props),
    item: baseStyleItem(props),
    itemIcon: baseStyleItemIcon(props),
  })),
});

export const useRadioButtonGroupStyleConfig = (
  props?: Parameters<typeof useMultiStyleConfig>[1],
): MultipartStyles<Parts> => {
  const path = `RadioButtonGroup`;

  return useMultiStyleConfig(path, props ?? {}) as MultipartStyles<Parts>;
};
